class WindyLoader {
  constructor() {
    this.loadPromise = null
    this.isLoaded = false
    this.loadError = null
  }

  async load() {
    // Return cached promise if already loading/loaded
    if (this.loadPromise) {
      return this.loadPromise
    }

    this.loadPromise = this._loadDependencies()
    return this.loadPromise
  }

  async _loadDependencies() {
    try {
      // Load dependencies sequentially to ensure proper order
      // 1. Load Leaflet first (required by MarkerCluster)
      await this._loadLeaflet()

      // 2. Load MarkerCluster after Leaflet is ready
      await this._loadLeafletCluster()

      // 3. Load Windy API (can be in parallel with MarkerCluster, but let's be safe)
      await this._loadWindyAPI()

      // Verify all dependencies are available
      await this._verifyDependencies()

      this.isLoaded = true
      return { success: true }
    } catch (error) {
      this.loadError = error.message
      console.error('Failed to load Windy dependencies:', error)

      return { success: false, error: error.message }
    }
  }

  async _loadLeaflet() {
    // Check if Leaflet is already loaded with correct version
    if (typeof L !== 'undefined') {
      const version = L.version
      if (version && version.startsWith('1.4.')) {
        // Still ensure CSS is loaded even if Leaflet exists
        await this._ensureLeafletCSS()
        return
      } else {
        console.warn(
          `Leaflet ${version} found but Windy requires 1.4.x - forcing reload of correct version`
        )
        // Clear the incompatible version
        delete window.L
      }
    }

    try {
      // Load CSS first and ensure it's fully applied
      await this._loadCSS('https://unpkg.com/leaflet@1.4.0/dist/leaflet.css')

      // Wait for CSS to be applied before loading JS
      await this._waitForCSS('leaflet.css')

      await this._loadScript('https://unpkg.com/leaflet@1.4.0/dist/leaflet.js')

      // Wait for Leaflet to be available
      await this._waitForGlobal('L', 5000)

      // Additional wait to ensure DOM and CSS are fully ready
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Verify correct version loaded
      if (typeof L !== 'undefined') {
        const version = L.version

        if (!version.startsWith('1.4.')) {
          throw new Error(`Expected Leaflet 1.4.x but got ${version}`)
        }
      } else {
        throw new Error('Leaflet failed to load from CDN')
      }
    } catch (error) {
      console.error('Failed to load Leaflet 1.4.0 from CDN:', error)
      throw error
    }
  }

  async _ensureLeafletCSS() {
    const cssLink = document.querySelector('link[href*="leaflet.css"]')
    if (!cssLink) {
      await this._loadCSS('https://unpkg.com/leaflet@1.4.0/dist/leaflet.css')
      await this._waitForCSS('leaflet.css')
    }
  }

  async _waitForCSS(cssIdentifier) {
    return new Promise((resolve) => {
      const checkCSS = () => {
        // Check if CSS is loaded by looking for Leaflet-specific styles
        const testElement = document.createElement('div')
        testElement.className = 'leaflet-control'
        testElement.style.position = 'absolute'
        testElement.style.visibility = 'hidden'
        document.body.appendChild(testElement)

        const styles = window.getComputedStyle(testElement)
        const hasLeafletStyles =
          styles.position === 'absolute' || styles.display !== 'inline'

        document.body.removeChild(testElement)

        if (hasLeafletStyles) {
          resolve()
        } else {
          setTimeout(checkCSS, 50)
        }
      }

      // Start checking after a brief delay
      setTimeout(checkCSS, 100)
    })
  }

  async _loadLeafletCluster() {
    // Ensure Leaflet is loaded first
    if (typeof L === 'undefined') {
      throw new Error('Leaflet must be loaded before MarkerCluster plugin')
    }

    if (L.markerClusterGroup) {
      return // Already loaded
    }

    try {
      // Try dynamic import first
      await import('leaflet.markercluster')
    } catch (error) {
      // Fallback to version compatible with Leaflet 1.4.x
      await Promise.all([
        this._loadScript(
          'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js'
        ),
        this._loadCSS(
          'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css'
        ),
        this._loadCSS(
          'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css'
        )
      ])
    }

    // Wait for marker cluster to be available
    await this._waitForCondition(
      () => typeof L !== 'undefined' && L.markerClusterGroup,
      5000,
      'MarkerCluster plugin'
    )
  }

  async _loadWindyAPI() {
    if (typeof windyInit !== 'undefined') {
      return // Already loaded
    }

    try {
      // Try loading from local lib first
      await import('../lib/libBoot.js')
    } catch (error) {
      // Fallback to Windy CDN
      await this._loadScript('https://api4.windy.com/assets/libBoot.js')
    }

    // Wait for windyInit to be available
    await this._waitForGlobal('windyInit', 10000)
  }

  async _verifyDependencies() {
    const missing = []

    if (typeof L === 'undefined') {
      missing.push('Leaflet')
    } else {
      // Verify Leaflet version is compatible with Windy (1.4.x)
      const version = L.version
      if (!version || !version.startsWith('1.4.')) {
        console.warn(
          `Windy requires Leaflet 1.4.x but found version ${version}`
        )
        // Don't throw error, but log warning - let Windy decide if it can work
      }
    }

    if (typeof L !== 'undefined' && !L.markerClusterGroup) {
      missing.push('Leaflet MarkerCluster')
    }

    if (typeof windyInit === 'undefined') {
      missing.push('Windy API')
    }

    // Check WebGL support
    if (!this._checkWebGLSupport()) {
      missing.push('WebGL support')
    }

    if (missing.length > 0) {
      throw new Error(`Missing dependencies: ${missing.join(', ')}`)
    }
  }

  _checkWebGLSupport() {
    try {
      const canvas = document.createElement('canvas')
      const gl =
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      return !!gl
    } catch (e) {
      return false
    }
  }

  _loadScript(src) {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
      document.head.appendChild(script)
    })
  }

  _loadCSS(href) {
    return new Promise((resolve, reject) => {
      // Check if CSS already exists
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve()
        return
      }

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.onload = resolve
      link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`))
      document.head.appendChild(link)
    })
  }

  _waitForGlobal(globalName, timeout = 5000) {
    return this._waitForCondition(
      () => typeof window[globalName] !== 'undefined',
      timeout,
      globalName
    )
  }

  _waitForCondition(condition, timeout = 5000, description = 'condition') {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkCondition = () => {
        if (condition()) {
          resolve()
          return
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error(`Timeout waiting for ${description}`))
          return
        }

        setTimeout(checkCondition, 100)
      }

      checkCondition()
    })
  }

  getStatus() {
    return {
      isLoaded: this.isLoaded,
      isLoading: !!this.loadPromise && !this.isLoaded,
      error: this.loadError
    }
  }

  reset() {
    this.loadPromise = null
    this.isLoaded = false
    this.loadError = null
  }
}

export const windyLoader = new WindyLoader()

export { WindyLoader }

export async function loadWindyDependencies() {
  return windyLoader.load()
}
